import { Injectable, Optional } from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

export interface PageContextResult {
    page_title: string;
    url: string;
    app_state_slice_key: string | null;
    app_state: object | null;
    dom_snapshot: string;
}

const DOM_SNAPSHOT_LIMIT = 50_000;
const TRUNCATION_NOTE = '\n<!-- truncated -->';

/**
 * Client-side handler for the `get_current_page_context` agent tool
 */
@Injectable({ providedIn: 'root' })
export class PageContextClientTool {
    constructor(@Optional() private readonly _store: Store | null) {}

    async execute(_input: unknown): Promise<PageContextResult> {
        const { sliceKey, sliceState } = await this._readStoreSlice();
        return {
            page_title: document.title,
            url: window.location.href,
            app_state_slice_key: sliceKey,
            app_state: sliceState,
            dom_snapshot: this._cleanDomSnapshot(document.body?.innerHTML ?? ''),
        };
    }

    async getCurrentSliceKey(): Promise<string | null> {
        const { sliceKey } = await this._readStoreSlice();
        return sliceKey;
    }

    private async _readStoreSlice(): Promise<{ sliceKey: string | null; sliceState: object | null }> {
        if (!this._store) {
            return { sliceKey: null, sliceState: null };
        }
        const snapshot: any = await firstValueFrom(this._store.select((state: any) => state).pipe(take(1)));
        const reducers = snapshot?.reducers ?? {};
        const sliceKey: string | null = reducers.appStateSliceKey ?? null;
        const sliceState = sliceKey && reducers[sliceKey] ? reducers[sliceKey] : null;
        return { sliceKey, sliceState };
    }

    private _cleanDomSnapshot(html: string): string {
        const container = document.createElement('div');
        container.innerHTML = html;
        // Strip non-content tags (scripts, styles, vectors, and app chrome)
        container
            .querySelectorAll('script, style, svg, ycn-top-bar-nav, ycn-agent-side-panel')
            .forEach((el) => el.remove());
        let cleaned = container.innerHTML
            // Strip noise attributes: framework internals, presentational, and tracking
            .replace(
                /\s(class|style|data-[\w-]+|_ngcontent-[\w-]+|_nghost-[\w-]+|ng-reflect-[\w-]+|aria-labelledby|aria-describedby)="[^"]*"/g,
                ''
            )
            // Empty attributes after stripping
            .replace(/\s(class|style)=""/g, '')
            // Collapse whitespace last
            .replace(/\s+/g, ' ')
            .trim();
        if (cleaned.length > DOM_SNAPSHOT_LIMIT) {
            cleaned = cleaned.slice(0, DOM_SNAPSHOT_LIMIT - TRUNCATION_NOTE.length) + TRUNCATION_NOTE;
        }
        return cleaned;
    }
}
